import { useLocalStorage } from "@elit-hair/elit-tools";
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Icon,
    Spinner,
} from "@elit-hair/elit-ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbCheck } from "react-icons/tb";

import { AudioDeviceSelector } from "@/components/audio-device-selector/audio-device-selector";
import { IosAudioRoutingHint } from "@/components/audio-device-selector/ios-audio-routing-hint";
import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { useCapabilities } from "@/features/audio";
import { useCall } from "@/providers/hooks/use-call";
import { soundService } from "@/services/soundService";

type AudioDeviceRequiredDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: () => void;
    onCancel: () => void;
};

/**
 * Modal shown when the user tries to call without having selected a microphone
 * or output device (speaker/headset). Lets them select both and then continue with the call.
 */
export function AudioDeviceRequiredDialog({ open, onOpenChange, onConfirm, onCancel }: AudioDeviceRequiredDialogProps) {
    const { t } = useTranslation();
    const { canSelectMicrophone, canSelectSpeaker } = useCapabilities();
    const { setInputDevice, setOutputDevice } = useCall();
    const [mediaDevices, setMediaDevices] = useState<MediaDeviceInfo[]>([]);
    const [loading, setLoading] = useState(false);

    const [selectedInputDeviceId, setSelectedInputDeviceId] = useLocalStorage<string | null>(
        LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.INPUT_DEVICE.key,
        LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.INPUT_DEVICE.default
    );

    const [selectedOutputDeviceId, setSelectedOutputDeviceId] = useLocalStorage<string | null>(
        LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.OUTPUT_DEVICE.key,
        LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.OUTPUT_DEVICE.default
    );

    const canContinue = Boolean(selectedInputDeviceId && (selectedOutputDeviceId || !canSelectSpeaker));

    useEffect(() => {
        if (!open || loading || canSelectSpeaker || selectedInputDeviceId) {
            return;
        }

        const defaultInput = mediaDevices.find((device) => device.kind === "audioinput");
        if (defaultInput) {
            setSelectedInputDeviceId(defaultInput.deviceId);
        }
    }, [canSelectSpeaker, loading, mediaDevices, open, selectedInputDeviceId, setSelectedInputDeviceId]);

    useEffect(() => {
        if (!open) return;

        const mediaDevicesApi = navigator.mediaDevices;
        if (!mediaDevicesApi) return;

        let stream: MediaStream | null = null;
        const loadDevices = async () => {
            setLoading(true);
            try {
                stream = await mediaDevicesApi.getUserMedia({ audio: true });
                const devices = await mediaDevicesApi.enumerateDevices();
                setMediaDevices(devices);
            } catch (error) {
                console.error("Error loading media devices:", error);
            } finally {
                if (stream) {
                    stream.getTracks().forEach((track) => track.stop());
                }
                setLoading(false);
            }
        };

        loadDevices();

        const handleDeviceChange = () => {
            mediaDevicesApi.enumerateDevices().then(setMediaDevices);
        };

        mediaDevicesApi.addEventListener("devicechange", handleDeviceChange);
        return () => {
            mediaDevicesApi.removeEventListener("devicechange", handleDeviceChange);
        };
    }, [open]);

    const handleConfirm = async () => {
        if (!canContinue || !selectedInputDeviceId) return;

        try {
            if (canSelectMicrophone) {
                await setInputDevice(selectedInputDeviceId);
            }
            if (canSelectSpeaker && selectedOutputDeviceId) {
                await setOutputDevice(selectedOutputDeviceId);
                soundService.setOutputDevice(selectedOutputDeviceId);
            }
        } catch (error) {
            console.error("Error applying audio devices:", error);
        }

        onConfirm();
        onOpenChange(false);
    };

    const handleCancel = () => {
        onCancel();
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={(next) => (!next ? handleCancel() : onOpenChange(next))}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{t("audio_device_required_title")}</DialogTitle>
                    <DialogDescription>
                        {canSelectSpeaker
                            ? t("audio_device_required_description")
                            : t("audio_device_required_ios_description")}
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="py-8 flex justify-center items-center">
                        <Spinner size="lg" />
                    </div>
                ) : (
                    <div className="space-y-4 py-2">
                        {!canSelectSpeaker && <IosAudioRoutingHint />}
                        {canSelectSpeaker && (
                            <AudioDeviceSelector
                                kind="audiooutput"
                                devices={mediaDevices}
                                selectedDeviceId={selectedOutputDeviceId}
                                onSelect={setSelectedOutputDeviceId}
                            />
                        )}
                        {canSelectMicrophone && (
                            <AudioDeviceSelector
                                kind="audioinput"
                                devices={mediaDevices}
                                selectedDeviceId={selectedInputDeviceId}
                                onSelect={setSelectedInputDeviceId}
                            />
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={handleCancel}>
                        {t("cancel")}
                    </Button>
                    <Button onClick={handleConfirm} disabled={!canContinue || loading}>
                        <Icon icon={TbCheck} className="mr-2 h-4 w-4" />
                        {t("select_devices_and_continue")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
